import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import type { Campaign, Disparo, DisparoStatus } from '../../types';
import { EditDisparoScheduleModal } from '../../components/EditDisparoScheduleModal/EditDisparoScheduleModal';
import './HistoricoDisparos.css';

const ACTIVE_HISTORICO_STATUSES: DisparoStatus[] = [
  'criado',
  'em_execucao',
  'pausado',
  'pausado_horario',
];

function getStatusLabel(status: string): string {
  const map: Record<string, string> = {
    criado: 'Criado',
    em_execucao: 'Em Execução',
    pausado: 'Pausado',
    pausado_horario: 'Fora do Horário',
    max_atingido: 'Limite Atingido',
    concluido: 'Concluído',
    cancelado: 'Cancelado',
  };
  return map[status] || status;
}

function getStatusClass(status: string): string {
  const map: Record<string, string> = {
    criado: 'status-criado',
    em_execucao: 'status-execucao',
    pausado: 'status-pausado',
    pausado_horario: 'status-horario',
    max_atingido: 'status-max',
    concluido: 'status-concluido',
    cancelado: 'status-cancelado',
  };
  return map[status] || '';
}

export const HistoricoDisparos: React.FC = () => {
  const [disparos, setDisparos] = useState<Disparo[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listFilter, setListFilter] = useState<'ativos' | 'todos'>('ativos');
  const [editingDisparo, setEditingDisparo] = useState<Disparo | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [disps, camps] = await Promise.all([
        apiService.getDisparos(),
        apiService.listCampaigns(),
      ]);
      setDisparos(disps);
      setCampaigns(camps);
    } catch {
      setError('Erro ao carregar histórico de disparos');
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(disparoId: string, newStatus: 'em_execucao' | 'pausado' | 'cancelado') {
    try {
      await apiService.updateDisparoStatus(disparoId, newStatus);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao atualizar status');
    }
  }

  async function handleCancelClick(disparoId: string) {
    if (!window.confirm('Cancelar este disparo? A campanha poderá receber um novo disparo.')) return;
    await handleStatusChange(disparoId, 'cancelado');
  }

  function getCampaignName(campaignId: string): string {
    return campaigns.find((c) => c.campaignId === campaignId)?.nome || 'Campanha removida';
  }

  const displayedDisparos = useMemo(() => {
    if (listFilter === 'todos') return disparos;
    return disparos.filter((d) => ACTIVE_HISTORICO_STATUSES.includes(d.status));
  }, [disparos, listFilter]);

  return (
    <div className="historico-disparos-page">
      <div className="historico-disparos-wrap">
        <div className="historico-disparos-header">
          <div>
            <h1>Histórico de Disparos</h1>
            <p>Acompanhe todos os disparos criados e seus status</p>
          </div>
          <div className="historico-disparos-filter" role="group" aria-label="Filtrar lista">
            <button
              type="button"
              className={`historico-filter-btn ${listFilter === 'ativos' ? 'active' : ''}`}
              onClick={() => setListFilter('ativos')}
            >
              Ativos
            </button>
            <button
              type="button"
              className={`historico-filter-btn ${listFilter === 'todos' ? 'active' : ''}`}
              onClick={() => setListFilter('todos')}
            >
              Todos
            </button>
          </div>
        </div>

        {error && <div className="historico-disparos-error">{error}</div>}

        {loading ? (
          <div className="historico-disparos-loading">Carregando...</div>
        ) : displayedDisparos.length === 0 ? (
          <div className="historico-disparos-empty">
            {disparos.length === 0 ? 'Nenhum disparo encontrado.' : 'Nenhum disparo neste filtro.'}
          </div>
        ) : (
          <div className="historico-disparos-table">
            <div className="historico-disparos-row historico-disparos-row-head">
              <span>Campanha</span>
              <span>Status</span>
              <span>Realizados</span>
              <span>Horário</span>
              <span>Entre envios</span>
              <span>Criado por</span>
              <span>Ações</span>
            </div>
            {displayedDisparos.map((d) => {
              const canPlay = ['criado', 'pausado', 'pausado_horario', 'max_atingido'].includes(d.status);
              const canPause = d.status === 'em_execucao';
              const canEditSchedule = ['criado', 'pausado', 'pausado_horario', 'em_execucao', 'max_atingido'].includes(d.status);
              const canCancel = ['criado', 'em_execucao', 'pausado', 'pausado_horario', 'max_atingido'].includes(
                d.status
              );

              return (
                <div key={d.disparoId} className="historico-disparos-row">
                  <span className="historico-cell-name">{getCampaignName(d.campaignId)}</span>
                  <span>
                    <span className={`disparo-status-badge ${getStatusClass(d.status)}`}>
                      {getStatusLabel(d.status)}
                    </span>
                  </span>
                  <span>{d.disparosRealizados}/{d.maxDisparosPorNumero}</span>
                  <span>{d.horaInicio}-{d.horaFim}</span>
                  <span>
                    {d.intervaloMinutos === 1
                      ? '1 min (~20–60 s)'
                      : `${d.intervaloMinutos} min`}
                  </span>
                  <span className="historico-cell-meta">{d.createdByNome || '—'}</span>
                  <span className="historico-row-actions">
                    {canEditSchedule && (
                      <button
                        type="button"
                        className="btn btn-sm btn-edit"
                        onClick={() => setEditingDisparo(d)}
                      >
                        Horário
                      </button>
                    )}
                    {canPlay && (
                      <button
                        type="button"
                        className="btn btn-sm btn-play"
                        onClick={() => handleStatusChange(d.disparoId, 'em_execucao')}
                      >
                        ▶ Play
                      </button>
                    )}
                    {canPause && (
                      <button
                        type="button"
                        className="btn btn-sm btn-pause"
                        onClick={() => handleStatusChange(d.disparoId, 'pausado')}
                      >
                        ⏸ Pause
                      </button>
                    )}
                    {canCancel && (
                      <button
                        type="button"
                        className="btn btn-sm btn-cancel-disp"
                        onClick={() => handleCancelClick(d.disparoId)}
                      >
                        Cancelar
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <EditDisparoScheduleModal
        open={!!editingDisparo}
        disparo={editingDisparo}
        onClose={() => setEditingDisparo(null)}
        onSaved={() => void loadData()}
      />
    </div>
  );
};
